// DS5.7
// @dynamic-i18n-keys
// siteFooter.menu
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { PapiUser } from '@wix/da-papi-types';
import { URLS, Url, sanitizeHref } from '@wix/da-url';
import { useSelectByTheme } from '@wix/da-react-context/pkg/ThemeContext';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { BiData, FooterClickBiEvent } from '@wix/da-bi/pkg/events';
import BiLink from '@wix/da-bi/pkg/Components/BiLink';
import VisuallyHidden from '@wix/da-a11y/pkg/components/VisuallyHidden';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import SolidButton from '@wix/da-ds/pkg/Buttons/SolidButton';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import Logo from '@wix/da-ds/pkg/Logo';
import { useRestrictToLoggedIn } from '../utils/hooks/useRestrictToLoggedIn';
import { useWatchButton } from '../WatchButton/useWatchButton';
import FooterCopyright from './FooterCopyright';
import { menuItems, socialButtons } from './constants';

import s from './SiteFooter.scss';

const TEAM_USER: PapiUser = {
  userId: 47880507,
  useridUuid: 'eae614f8-f83b-4837-935b-ab22f4298eb2',
  username: 'team',
  type: 'admin',
} as PapiUser;

export interface Props {
  className?: string;
}

const SiteFooter: React.FC<Props> = ({ className }) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);

  const logoColor = useSelectByTheme({
    dark: 'white',
    light: 'black',
    green: 'white',
  } as const);

  const numLinkColumns = isMobile ? 2 : 3;
  // might want to make this 3 on ultra-wide (500+) mobile
  const linksPerColumn = Math.ceil(menuItems.length / numLinkColumns);
  const linkColumns: any[] = [];
  for (let i = 0; i < menuItems.length; i += linksPerColumn) {
    linkColumns.push(menuItems.slice(i, i + linksPerColumn));
  }

  const { restrictToLoggedIn } = useRestrictToLoggedIn();
  const onSuccessfulWatch = isWatching => {
    if (isWatching && typeof window !== 'undefined') {
      window.location.assign(Url.userLink(TEAM_USER.username));
    }
  };
  const { onClick: onWatch, biData } = useWatchButton(
    TEAM_USER,
    false,
    false,
    false,
    onSuccessfulWatch,
    { evid: 380, sectionname: 'siteFooter' } // WatchAuthorBiEvent
  );
  const onClickWatch = restrictToLoggedIn(ReferralType.SITE_FOOTER, onWatch);

  return (
    <footer className={classnames(s['root'], className)} role="contentinfo">
      <div className={s['main-content']}>
        <div className={classnames(s['column'], s['left'])}>
          <BiLink
            href={URLS.ROOT}
            title={t('siteFooter.homepage.title')}
            biData={BiData<FooterClickBiEvent>({
              evid: 720,
              link_name: 'logo',
            })}
          >
            <VisuallyHidden>{t('siteFooter.homepage.title')}</VisuallyHidden>
            <Logo markColor={logoColor} textColor={logoColor} size="tiny" />
          </BiLink>

          <div className={s['social']}>
            {socialButtons.map((sb, index) => (
              <BiLink
                key={sb.title}
                href={sanitizeHref(sb.href)}
                title={sb.title}
                biData={BiData<FooterClickBiEvent>({
                  evid: 720,
                  link_name: sb.name,
                })}
              >
                <VisuallyHidden>{sb.title}</VisuallyHidden>
                <sb.icon size={IconSize.MEDIUM} disableDefaultColors />
              </BiLink>
            ))}
          </div>
        </div>

        {linkColumns.map((columnItems, i) => (
          <div key={`column-${i}`} className={s['column']}>
            {columnItems.map(item => (
              <BiLink
                key={item.key}
                className={s['link']}
                href={sanitizeHref(item.href)}
                title={t(item.key)}
                biData={BiData<FooterClickBiEvent>({
                  evid: 720,
                  link_name: item.name,
                })}
              >
                {t(item.mobileKey && isMobile ? item.mobileKey : item.key)}
              </BiLink>
            ))}
          </div>
        ))}

        <div className={classnames(s['column'], s['right'])}>
          <div className={s['watch-team-description']}>
            {t('siteFooter.watchTeam.description', {
              postProcess: ['reactPostprocessor'],
              team: (
                <BiLink className={s['logo-before']} href={URLS.TEAM}>
                  Team
                </BiLink>
              ),
              community: <BiLink href={URLS.COMMUNITY}>Community</BiLink>,
            })}
          </div>

          <div className={s['cta-stack']}>
            <BrandedButton
              size="small"
              variant="brand-green"
              biData={biData}
              onClick={onClickWatch}
            >
              {t('siteFooter.watchTeam.buttonText')}
            </BrandedButton>
            <SolidButton
              size="small"
              variant="main"
              priority="secondary"
              biData={biData}
              href={Url.userLink('community')}
            >
              {t('siteFooter.joinCommunity.buttonText')}
            </SolidButton>
          </div>
        </div>
      </div>

      <FooterCopyright />
    </footer>
  );
};
SiteFooter.displayName = 'SiteFooter';

export default React.memo(SiteFooter);
